<template>
    <div>
        <b-modal
            :visible="true"
            id="feedbackDeleteModal"
            no-close-on-esc
            no-close-on-backdrop
            no-fade
            hide-footer
            hide-header
            :dialog-class="
                deletingSingle
                    ? 'deletefeedbackSingle'
                    : 'deletefeedbackMultiple'
            "
        >
            <div>
                <!-- Header -->
                <div class="panel-header">
                    <h3 class="panel-header-title">{{ titleText }}</h3>
                    <!-- Close icon -->
                    <icon-close
                        :id="'mobile-delete-item'"
                        @close="$_close"
                        class="d-inline-block float-right"
                    >
                    </icon-close>
                </div>
                <!-- Info message -->
                <div v-if="deletingSingle" class="delete-info">
                    <p>{{ infoText }}</p>
                </div>
                <!-- Content -->
                <div class="delete-content">
                    <div id="deletefeedback_content" v-if="deletingSingle">
                        <object-detail :displayObject="firstObject" />
                    </div>
                    <!-- Multiple feedback -->
                    <div v-else-if="deletingMultiple">
                        <p>{{ confirmText }}</p>
                        <p>
                            {{
                                $t('deleteFeedbackConfirmationMessageMultiple2')
                            }}
                        </p>
                    </div>
                    <!-- Buttons -->
                    <div id="deletefeedback_buttons">
                        <b-button
                            v-bind:class="{ saving: isSaving }"
                            variant="primary"
                            @click.stop="$_doDelete"
                            :disabled="isSaving"
                        >
                            {{ $t('btnDeleteLabel') }}&nbsp;<span
                                class="far"
                                v-bind:class="{
                                    'fa fa-sync fa-spin': isSaving
                                }"
                            >
                            </span>
                        </b-button>
                        <b-button
                            v-bind:class="{ saving: isSaving }"
                            :disabled="isSaving"
                            @click="$_close"
                        >
                            {{ $t('btnCancelLabel') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import { mapActions, mapGetters, mapMutations } from 'vuex';
    import { CONTENT_TYPE_ID, SELECTION_TYPES } from '../../../../constants';
    import { objectData } from '../../../../services/selections';

    import IconClose from '../../../IconClose.vue';
    import ObjectDetail from '../../information/feature/ObjectDetail';
    import modalMixin from '../../../../components/Mixins/ModalMixin.vue';

    export default {
        name: 'FeedbackDelete',
        components: {
            'icon-close': IconClose,
            'object-detail': ObjectDetail
        },

        props: { deletefeedbacks: { type: Object, required: true } },
        created() {
            this.$_initializeFeedbackData();
        },
        data() {
            return {
                titleText: '',
                infoText: '',
                confirmText: '',
                userInfo: this.$store.getters.userInfo,
                isSaving: false,
                deleteFeedbackTheme: {},
                contentTypeId: -1
            };
        },
        mixins: [modalMixin],

        computed: {
            ...mapGetters([
                'isMobile',
                'getUserData',
                'getSelectionData',
                'getLocale'
            ]),

            deletingSingle() {
                return this.deleteCount === 1;
            },

            deletingMultiple() {
                return this.deleteCount > 1;
            },

            deleteCount() {
                return this.deletefeedbacks?.features?.length || 0;
            },
            firstObject() {
                const { features, layer } = this.deletefeedbacks;
                return objectData(
                    features,
                    layer,
                    this.isMobile,
                    false,
                    this.getLocale
                )?.[0];
            }
        },

        methods: {
            ...mapActions(['displaySelectedItem']),
            ...mapMutations(['ADD_SELECTION']),

            $_close() {
                this.$emit('cancel');
            },

            $_initializeFeedbackData() {
                if (this.deletefeedbacks.layer.data?.feedbackTypeId === 0) {
                    this.contentTypeId = CONTENT_TYPE_ID.WFS_T;
                } else {
                    this.deleteFeedbackTheme = this.$store.getters.getFeedbackById(
                        this.deletefeedbacks.layer.extended.feedbackId
                    )?.data;
                    this.contentTypeId = this.deleteFeedbackTheme.feedbackType.contentTypeId;
                }
                this.$_setText(this.contentTypeId);
            },

            $_setText(contentTypeId) {
                if (contentTypeId === CONTENT_TYPE_ID.FEEDBACK) {
                    this.titleText = this.deletingSingle
                        ? this.$t('deleteFeedbackTitleSingle')
                        : this.$t('deleteFeedbackTitleMultiple');
                    this.infoText = this.$t(
                        'deleteFeedbackConfirmationMessageSingle'
                    );
                    this.confirmText = this.$t(
                        'deleteFeedbackConfirmationMessageMultiple1',
                        [`${this.deleteCount}`]
                    );
                } else if (contentTypeId === CONTENT_TYPE_ID.WFS_T) {
                    this.titleText = this.deletingSingle
                        ? this.$t('deleteWfsTTitleSingle')
                        : this.$t('deleteWfsTTitleMultiple');
                    this.infoText = this.$t(
                        'deleteWfsTConfirmationMessageSingle'
                    );
                    this.confirmText = this.$t(
                        'deleteWfsTConfirmationMessageMultiple',
                        [`${this.deleteCount}`]
                    );
                } else {
                    this.titleText = this.deletingSingle
                        ? this.$t('deleteMeasureTitleSingle')
                        : this.$t('deleteMeasureTitleMultiple');
                    this.infoText = this.$t(
                        'deleteMeasurementConfirmationMessageSingle'
                    );
                    this.confirmText = this.$t(
                        'deleteMeasuremenConfirmationMessageMultiple1',
                        [`${this.deleteCount}`]
                    );
                }
            },
            $_doDelete() {
                if (this.contentTypeId == CONTENT_TYPE_ID.WFS_T) {
                    this.$_doDeleteWfsT();
                } else {
                    this.$_doDeleteFeedback();
                }
            },
            /**
             * Delete a feedback in mobile or web.
             * @return {object} [promise object]
             */
            async _deleteFeedback() {
                let deleteFeedbackParams;
                let name;

                if (this.isMobile) {
                    // Authentication is required, this is the Mobile app
                    name = 'DeleteFeedbacks';
                    deleteFeedbackParams = {
                        feedbackFeatureIds: this.$_getEncodedFeatureIds(),
                        deletedBy: this.getUserData.userName
                    };
                } else {
                    // No authentication is required, this is the Web app
                    name = 'DeleteWebFeedbacks';
                    deleteFeedbackParams = {
                        feedbackFeatureIds: this.$_getEncodedFeatureIds(),
                        webThemeId: this.$store.getters.getWebTheme.id,
                        deletedBy: this.$store.getters.getUserInfo.sessionId
                    };
                }

                const result = await this.$store.dispatch('useApiData', {
                    name: name,
                    data: deleteFeedbackParams
                });
                return result;
            },

            $_doDeleteFeedback() {
                this.isSaving = true;

                this._deleteFeedback()
                    .then((result) => {
                        if (result) {
                            this.$store.dispatch('toggleFeedbackLayer', {
                                id: this.deletefeedbacks.layer.id,
                                active: true
                            });
                            this.$_updateSelection();
                        }
                        this.isSaving = false;
                        this.$emit('finished');
                    })
                    .catch((error) => {
                        this.showToast({
                            title: this.$t('saveFailureTitle'),
                            message: this.$t(error.message),
                            variant: 'danger',
                            autoHideDelay: 10000
                        });
                        this.isSaving = false;
                        this.$_close();
                    });
            },
            async _deleteWfsT() {
                let deleteFeedbackParams;
                if (this.isMobile) {
                    // Authentication is required, this is the Mobile app
                    let fIds = this.$_getEncodedWfsTIds();
                    deleteFeedbackParams = {
                        WfsTFeatureIds: fIds,
                        DeletedBy: this.getUserData.userName,
                        Id: this.deletefeedbacks.layer.data.id
                    };
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'DeleteFeedbacksWfsT',
                        data: deleteFeedbackParams
                    });
                    return result;
                }
                return null;
            },
            $_doDeleteWfsT() {
                this.isSaving = true;

                this._deleteWfsT()
                    .then((result) => {
                        if (result) {
                            this.$store.dispatch('toggleFeedbackLayer', {
                                id: this.deletefeedbacks.layer.id,
                                active: true
                            });
                            this.$_updateSelection();
                        }
                        this.isSaving = false;
                        this.$emit('finished');
                    })
                    .catch((error) => {
                        this.showToast({
                            title: this.$t('saveFailureTitle'),
                            message: this.$t(error.message),
                            variant: 'danger',
                            autoHideDelay: 10000
                        });
                        this.isSaving = false;
                        this.$_close();
                    });
            },
            $_getEncodedFeatureIds() {
                return this.deletefeedbacks.features.map((fb) =>
                    Buffer.from(fb.getId()).toString('base64')
                );
            },
            $_getEncodedWfsTIds() {
                return this.deletefeedbacks.features.map((fb) =>
                    Buffer.from(fb.id_).toString('base64')
                );
            },
            $_updateSelection() {
                const selectionData = { ...this.getSelectionData };
                const deletedIds = this.deletefeedbacks.features.map((fb) =>
                    fb.getId()
                );

                const results = selectionData?.results || [];
                const newSelection = results.map((result) => {
                    return {
                        ...result,
                        features: result.features.filter(
                            (feat) => !deletedIds.includes(feat.id_)
                        )
                    };
                });
                this.ADD_SELECTION({
                    location: null,
                    results: newSelection,
                    type: SELECTION_TYPES.SELECTION,
                    deletedState: true
                });
                this.displaySelectedItem({});
            }
        }
    };
</script>

<style>
#feedbackDeleteModal .modal-dialog {
    position: absolute;
    margin: 0px;
    top: 15%;
    left: calc(50% - 300px);
    min-height: 40px;
    min-width: 600px;
    max-width: 600px;
}

#feedbackDeleteModal .modal-dialog.deletefeedbackSingle {
    display: flex !important;
    flex-direction: column;
    bottom: 15%;
}

#feedbackDeleteModal .modal-dialog.deletefeedbackSingle .modal-body > div {
    height: 100%;
    display: flex !important;
    flex-direction: column;
}

#feedbackDeleteModal .modal-dialog .modal-content {
    height: 100%;
    border: none;
    border-radius: initial;
}

#feedbackDeleteModal .modal-body {
    padding: 0px;
    height: 100%;
}

.firstCol {
    padding: 4px !important;
}

.lastCol {
    font-weight: bold;
    padding: 4px !important;
}

@media (max-width: 767px) {
    #feedbackDeleteModal .modal-dialog {
        top: 0 !important;
        left: 0%;
        bottom: 0 !important;
        right: 0;
        min-width: 0;
        max-width: none;
    }

    #feedbackDeleteModal
        .modal-dialog.deletefeedbackMultiple
        .modal-body
        > div {
        height: 100%;
    }
}
</style>

<style scoped>
.delete-info {
    padding: 20px 20px 0px 20px;
}

.delete-info p {
    margin-bottom: 0;
}

.delete-content {
    display: flex !important;
    flex-direction: column;
    overflow-y: auto;
    padding: 20px;
    flex: 1;
}

#deletefeedback_content {
    overflow-x: hidden;
    background: #ffffff;
    padding-left: 8px;
    flex: 1;
}

#deletefeedback_buttons {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
}

#deletefeedback_buttons > button:first-child {
    margin-right: 3px;
}
</style>
